import React, { useCallback, useEffect, useState } from 'react'
import {
  Bot,
  CircleDollarSign,
  LoaderCircle,
  Save,
  Users,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function formatNumber(value, maximumFractionDigits = 1) {
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits }).format(Number(value) || 0)
}

function Meter({ label, value, limit, percent, unit }) {
  const safePercent = Math.max(0, Math.min(100, Number(percent) || 0))
  const tone = safePercent >= 90 ? 'bg-rose-500' : safePercent >= 70 ? 'bg-amber-500' : 'bg-sky-600'
  return (
    <div className="px-4 py-4">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="shrink-0 text-slate-600">{formatNumber(value)} / {formatNumber(limit)}{unit}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded bg-slate-100" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={safePercent}>
        <div className={`h-full ${tone}`} style={{ width: `${safePercent}%` }} />
      </div>
      <div className="mt-1 text-right text-xs text-slate-500">{formatNumber(safePercent)}%</div>
    </div>
  )
}

const EMPTY_FORM = {
  dailyUserSessionLimit: 30,
  userConcurrentSessionLimit: 3,
  orgConcurrentSessionLimit: 20,
  monthlyAiTokenLimit: 5000000,
  monthlyAiBudgetKrw: 50000,
  maxRepetitionRetries: 1,
}

export default function CounselPracticeUsagePolicy() {
  const [data, setData] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const applySnapshot = (snapshot) => {
    setData(snapshot)
    setForm({ ...EMPTY_FORM, ...(snapshot?.policy || {}) })
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      applySnapshot(unwrap(await api.get('/counsel-practice/admin/usage-policy', { __silent: true })))
    } catch (e) {
      setError(getApiErrorMessage(e, '사용 한도를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const setValue = (name, value) => setForm((current) => ({ ...current, [name]: value }))

  const save = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, Number(value)]))
      applySnapshot(unwrap(await api.put('/counsel-practice/admin/usage-policy', payload, { __silent: true })))
      setMessage('사용 한도와 AI 예산을 저장했습니다.')
    } catch (e) {
      setError(getApiErrorMessage(e, '사용 한도를 저장하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="flex min-h-64 items-center justify-center text-slate-500"><LoaderCircle className="h-6 w-6 animate-spin" aria-label="사용 한도 불러오는 중" /></div>
  }

  const usage = data?.usage || {}
  const policy = data?.policy || EMPTY_FORM

  return (
    <section aria-labelledby="usage-policy-title" data-testid="counsel-usage-policy">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-2">
          <CircleDollarSign className="h-5 w-5 text-emerald-700" aria-hidden="true" />
          <h2 id="usage-policy-title" className="text-lg font-semibold text-slate-950">사용 한도와 AI 예산</h2>
        </div>
        <p className="mt-1 text-sm text-slate-600">이용이 몰리거나 월 예산을 넘으면 기본 고객 답변으로 연습을 이어갑니다.</p>
      </div>

      {error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{error}</div>}
      {message && <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" role="status">{message}</div>}

      <div className="mt-5 grid border-y border-slate-200 lg:grid-cols-3 lg:divide-x lg:divide-slate-200">
        <Meter label="오늘 내 연습" value={usage.dailyUserSessions} limit={policy.dailyUserSessionLimit} percent={usage.dailyUserSessionPercent} unit="회" />
        <Meter label="이번 달 AI 사용량" value={usage.monthlyAiTokens} limit={policy.monthlyAiTokenLimit} percent={usage.monthlyAiTokenPercent} unit=" 토큰" />
        <Meter label="이번 달 예상 비용" value={usage.monthlyAiCostKrw} limit={policy.monthlyAiBudgetKrw} percent={usage.monthlyAiBudgetPercent} unit="원" />
      </div>

      <form className="mt-6" onSubmit={save}>
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
          <label className="block">
            <span className="flex items-center gap-2 text-sm font-medium text-slate-700"><Users className="h-4 w-4 text-sky-700" aria-hidden="true" />하루 개인 연습 한도</span>
            <input type="number" min={1} max={1000} value={form.dailyUserSessionLimit} onChange={(event) => setValue('dailyUserSessionLimit', event.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">개인 동시 연습 수</span>
            <input type="number" min={1} max={100} value={form.userConcurrentSessionLimit} onChange={(event) => setValue('userConcurrentSessionLimit', event.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">조직 동시 연습 수</span>
            <input type="number" min={1} max={1000} value={form.orgConcurrentSessionLimit} onChange={(event) => setValue('orgConcurrentSessionLimit', event.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="flex items-center gap-2 text-sm font-medium text-slate-700"><Bot className="h-4 w-4 text-indigo-700" aria-hidden="true" />월 AI 토큰 한도</span>
            <input type="number" min={0} max={10000000000} step={1000} value={form.monthlyAiTokenLimit} onChange={(event) => setValue('monthlyAiTokenLimit', event.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">월 AI 예산</span>
            <div className="relative mt-1.5"><input type="number" min={0} max={1000000000} step={1000} value={form.monthlyAiBudgetKrw} onChange={(event) => setValue('monthlyAiBudgetKrw', event.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 pr-10 text-sm" /><span className="absolute inset-y-0 right-3 flex items-center text-xs text-slate-500">원</span></div>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">품질 미달 답변 다시 만들기</span>
            <select value={form.maxRepetitionRetries} onChange={(event) => setValue('maxRepetitionRetries', event.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
              <option value={0}>다시 만들지 않음</option>
              <option value={1}>최대 1회</option>
              <option value={2}>최대 2회</option>
              <option value={3}>최대 3회</option>
            </select>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
          <div className="text-xs text-slate-500">현재 진행 중: 개인 {formatNumber(usage.userConcurrentSessions)}개 · 조직 {formatNumber(usage.orgConcurrentSessions)}개</div>
          <button type="submit" disabled={saving} className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
            {saving ? '저장 중' : '설정 저장'}
          </button>
        </div>
      </form>
    </section>
  )
}
